import { useEffect, useState } from 'react'
import { theme } from '../theme'
import { api } from '../api'
import { useSearch, useNavigate } from '../router'
import { SLOT_KEYS, SLOT_LABELS, DOW_LONG, DOW_SHORT } from '../dates'
import type { Me, MassUnits, VolumeUnits, TempUnits, SlotMask, SlotMaskCell, SlotKey, Rule, ChatModel, ChatModelsResponse } from '../types'
import { RulesEditor } from './RulesEditor'
import { AccountSharing } from './AccountSharing'

const SCALES: { value: number; label: string }[] = [
  { value: 0.9,  label: 'Smaller' },
  { value: 1.0,  label: 'Default' },
  { value: 1.15, label: 'Larger' },
  { value: 1.3,  label: 'Largest' },
]

const MASS_OPTIONS: { value: MassUnits; label: string }[] = [
  { value: 'imperial', label: 'Imperial (oz, lb)' },
  { value: 'metric',   label: 'Metric (g, kg)' },
]

const VOLUME_OPTIONS: { value: VolumeUnits; label: string }[] = [
  { value: 'imperial', label: 'Imperial (tsp, cup)' },
  { value: 'metric',   label: 'Metric (ml, l)' },
]

const TEMP_OPTIONS: { value: TempUnits; label: string }[] = [
  { value: 'F', label: '°F' },
  { value: 'C', label: '°C' },
]

function Section({ title, subtitle, children }: {
  title: string; subtitle?: string; children: React.ReactNode
}) {
  return (
    <section style={{
      background: theme.surface, border: theme.cardBorder,
      borderRadius: theme.radiusLg, padding: 20, marginBottom: 16,
    }}>
      <div style={{
        fontFamily: theme.serif, fontWeight: theme.headWeight,
        fontSize: 20, color: theme.ink, marginBottom: 4,
      }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: 14, color: theme.inkSoft, marginBottom: 16 }}>
          {subtitle}
        </div>
      )}
      {children}
    </section>
  )
}

function SubTabs({ value, onChange }: {
  value: 'personalization' | 'advanced'
  onChange: (v: 'personalization' | 'advanced') => void
}) {
  const tab = (v: 'personalization' | 'advanced'): React.CSSProperties => ({
    all: 'unset', cursor: 'pointer',
    padding: '8px 14px',
    fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: value === v ? theme.ink : theme.muted,
    borderBottom: value === v ? `2px solid ${theme.ink}` : `2px solid transparent`,
    marginBottom: -1,
  })
  return (
    <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${theme.line}` }}>
      <button style={tab('personalization')} onClick={() => onChange('personalization')}>
        Personalization
      </button>
      <button style={tab('advanced')} onClick={() => onChange('advanced')}>
        Advanced
      </button>
    </div>
  )
}

function PillGroup<T extends string | number>({
  options, current, disabled, onPick,
}: {
  options: { value: T; label: string }[]
  current: T
  disabled: boolean
  onPick: (v: T) => void
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map(o => {
        const active = typeof o.value === 'number' && typeof current === 'number'
          ? Math.abs((o.value as number) - (current as number)) < 0.001
          : o.value === current
        return (
          <button
            key={String(o.value)}
            onClick={() => onPick(o.value)}
            disabled={disabled}
            style={{
              all: 'unset', cursor: disabled ? 'default' : 'pointer',
              padding: '10px 16px', borderRadius: theme.radius,
              border: `1px solid ${active ? theme.ink : theme.line}`,
              background: active ? theme.ink : theme.surface,
              color: active ? theme.surface : theme.ink,
              fontFamily: theme.sans, fontSize: 14, fontWeight: 500,
            }}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

const EFFORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'off',     label: 'Off' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'low',     label: 'Low' },
  { value: 'medium',  label: 'Medium' },
  { value: 'high',    label: 'High' },
]

// priceLabel renders a model's input/output cost per million tokens, or '' when
// pricing isn't available yet (OpenRouter fetch failed/pending).
function priceLabel(m: ChatModel): string {
  if (m.input_per_mtok == null || m.output_per_mtok == null) return ''
  const f = (n: number) => '$' + n.toFixed(n < 1 ? 3 : 2)
  return `${f(m.input_per_mtok)} in / ${f(m.output_per_mtok)} out per 1M tok`
}

// ModelSelect is a native dropdown whose every option carries the model's
// per-Mtok pricing inline. A blank stored value means "inherit the server
// default," so we display defaultId as the effective selection.
function ModelSelect({ models, value, defaultId, disabled, onPick }: {
  models: ChatModel[]; value: string; defaultId: string; disabled: boolean; onPick: (id: string) => void
}) {
  return (
    <select
      value={value || defaultId}
      disabled={disabled}
      onChange={e => onPick(e.target.value)}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '10px 12px', borderRadius: theme.radius,
        border: `1px solid ${theme.line}`, background: theme.surface,
        color: theme.ink, fontFamily: theme.sans, fontSize: 14,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {models.map(m => {
        const price = priceLabel(m)
        return (
          <option key={m.id} value={m.id}>
            {m.display_name}{price ? ` — ${price}` : ''}
          </option>
        )
      })}
    </select>
  )
}

function SubBlock({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode
}) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, color: theme.ink, marginBottom: hint ? 4 : 8 }}>{label}</div>
      {hint && <div style={{ fontSize: 13, color: theme.inkSoft, marginBottom: 8 }}>{hint}</div>}
      {children}
    </div>
  )
}

// ChatModelSettings is the Advanced panel for choosing which model + thinking
// level the agent uses for regular chat vs. tours. Each setting is blank by
// default (inherits the server default shown as the active selection); picking
// a value pins it.
function ChatModelSettings({ me, patch, disabled }: {
  me: Me
  patch: (body: Parameters<typeof api.updateMe>[0]) => void
  disabled: boolean
}) {
  const [data, setData] = useState<ChatModelsResponse | null>(null)
  const [err, setErr] = useState(false)
  useEffect(() => {
    let live = true
    api.chatModels().then(d => { if (live) setData(d) }).catch(() => { if (live) setErr(true) })
    return () => { live = false }
  }, [])

  if (err) return <div style={{ fontSize: 13, color: theme.inkSoft }}>Couldn’t load the model list.</div>
  if (!data) return <div style={{ fontSize: 13, color: theme.inkSoft }}>Loading models…</div>
  const d = data.defaults

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <SubBlock label="Regular chat — model">
        <ModelSelect models={data.models} value={me.chat_model} defaultId={d.regular_model}
          disabled={disabled} onPick={id => patch({ chat_model: id })}/>
      </SubBlock>
      <SubBlock label="Regular chat — thinking">
        <PillGroup options={EFFORT_OPTIONS} current={me.chat_effort || d.regular_effort}
          disabled={disabled} onPick={v => patch({ chat_effort: v })}/>
      </SubBlock>

      <SubBlock label="Tours — model" hint="Used for the proactive greeting and the guided-tour walk.">
        <ModelSelect models={data.models} value={me.tour_model} defaultId={d.tour_model}
          disabled={disabled} onPick={id => patch({ tour_model: id })}/>
      </SubBlock>
      <SubBlock label="Tours — walk thinking" hint="Applies while you still have pending tours.">
        <PillGroup options={EFFORT_OPTIONS} current={me.tour_effort || d.tour_effort}
          disabled={disabled} onPick={v => patch({ tour_effort: v })}/>
      </SubBlock>
      <SubBlock label="Tours — greeting thinking" hint="The throwaway proactive greeting; cheaper is usually fine.">
        <PillGroup options={EFFORT_OPTIONS} current={me.greeting_effort || d.greeting_effort}
          disabled={disabled} onPick={v => patch({ greeting_effort: v })}/>
      </SubBlock>
    </div>
  )
}

export function PreferencesTab({ me, onMeChange, slotMask, onSlotMaskChange, rules, mobile }: {
  me: Me
  onMeChange: (m: Me) => void
  slotMask: SlotMask | null
  onSlotMaskChange: (m: SlotMask) => void
  rules: Rule[]
  mobile: boolean
}) {
  const [saving, setSaving] = useState(false)
  const [dietDraft, setDietDraft] = useState(me.dietary_restrictions)
  const [dietSaving, setDietSaving] = useState(false)
  const [dietSavedAt, setDietSavedAt] = useState<number | null>(null)

  // The focused sub-tab lives in the URL (`/preferences?tab=personalization|advanced`)
  // so the agent can deep-link to either via `navigate`. URL is the single
  // source of truth; absence of the param means "personalization". Toggling
  // replaces history so sub-tab flips don't pile up on the back stack.
  const navigate = useNavigate()
  const search = useSearch()
  const subTab: 'personalization' | 'advanced' =
    new URLSearchParams(search).get('tab') === 'advanced' ? 'advanced' : 'personalization'
  const setSubTab = (v: 'personalization' | 'advanced') => navigate(`/preferences?tab=${v}`, { replace: true })

  useEffect(() => { setDietDraft(me.dietary_restrictions) }, [me.dietary_restrictions])

  const patch = async (body: Parameters<typeof api.updateMe>[0]) => {
    if (saving) return
    const prev = me
    onMeChange({ ...me, ...body } as Me)
    setSaving(true)
    try {
      const updated = await api.updateMe(body)
      onMeChange(updated)
    } catch (e) {
      console.error(e)
      onMeChange(prev)
    } finally {
      setSaving(false)
    }
  }

  const saveDiet = async () => {
    if (dietSaving || dietDraft === me.dietary_restrictions) return
    setDietSaving(true)
    try {
      const updated = await api.updateMe({ dietary_restrictions: dietDraft })
      onMeChange(updated)
      setDietSavedAt(Date.now())
    } catch (e) {
      console.error(e)
    } finally {
      setDietSaving(false)
    }
  }

  const dietDirty = dietDraft !== me.dietary_restrictions

  return (
    <div style={{ maxWidth: 640 }}>
      <h1 style={{
        fontFamily: theme.serif, fontWeight: theme.headWeight,
        fontSize: 32, margin: '4px 0 24px', color: theme.ink,
      }}>Preferences</h1>

      <div style={{ marginBottom: 20 }}>
        <SubTabs value={subTab} onChange={setSubTab} />
      </div>

      {subTab === 'personalization' && (
        <>
          <Section title="Display size" subtitle="Scales the entire app — text, buttons, images.">
            <PillGroup
              options={SCALES}
              current={me.ui_scale || 1.0}
              disabled={saving}
              onPick={v => patch({ ui_scale: v })}
            />
          </Section>

          <Section title="Mass units" subtitle="How Mila presents weights when suggesting recipes or pantry quantities.">
            <PillGroup
              options={MASS_OPTIONS}
              current={me.mass_units}
              disabled={saving}
              onPick={v => patch({ mass_units: v })}
            />
          </Section>

          <Section title="Volume units" subtitle="How Mila presents volumes (liquids, scoops).">
            <PillGroup
              options={VOLUME_OPTIONS}
              current={me.volume_units}
              disabled={saving}
              onPick={v => patch({ volume_units: v })}
            />
          </Section>

          <Section title="Temperature units" subtitle="Used for oven temperatures and cooking instructions.">
            <PillGroup
              options={TEMP_OPTIONS}
              current={me.temp_units}
              disabled={saving}
              onPick={v => patch({ temp_units: v })}
            />
          </Section>

          <Section title="Planner mask" subtitle="Which slots the planner fills by default when you click “Plan week.” Tap a cell to toggle. The planner skips off cells; you can still fill them manually.">
            <SlotMaskGrid mask={slotMask} onChange={onSlotMaskChange} mobile={mobile}/>
          </Section>

          <Section title="Dietary restrictions" subtitle="Free-form notes Mila considers when suggesting or planning meals (e.g. allergies, vegetarian, no pork).">
            <textarea
              value={dietDraft}
              onChange={e => setDietDraft(e.target.value)}
              placeholder="e.g. lactose intolerant, no shellfish"
              rows={4}
              maxLength={2000}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', borderRadius: theme.radius,
                border: `1px solid ${theme.line}`,
                background: theme.surface, color: theme.ink,
                fontFamily: theme.sans, fontSize: 14, resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
              <button
                onClick={saveDiet}
                disabled={dietSaving || !dietDirty}
                style={{
                  all: 'unset',
                  cursor: dietSaving || !dietDirty ? 'default' : 'pointer',
                  padding: '10px 16px', borderRadius: theme.radius,
                  background: dietDirty ? theme.ink : theme.line,
                  color: dietDirty ? theme.surface : theme.inkSoft,
                  fontFamily: theme.sans, fontSize: 14, fontWeight: 500,
                }}
              >
                {dietSaving ? 'Saving…' : 'Save'}
              </button>
              {!dietDirty && dietSavedAt && Date.now() - dietSavedAt < 4000 && (
                <span style={{ fontSize: 13, color: theme.inkSoft }}>Saved</span>
              )}
            </div>
          </Section>
        </>
      )}

      {subTab === 'advanced' && (
        <>
          <Section title="Chat models" subtitle="Which model and thinking level the agent uses. Tours (the proactive greeting + guided walk) can run on a different model than regular chat. Prices are per million tokens.">
            <ChatModelSettings me={me} patch={patch} disabled={saving} />
          </Section>

          <Section title="Planning rules" subtitle="Custom rules that bias or constrain the planner. The chat agent edits these too; this is the manual surface.">
            <RulesEditor rules={rules} />
          </Section>

          <Section title="Account sharing" subtitle="Grant someone access to your account, or switch into an account shared with you. They use their own login; everything they touch while managing your account is yours.">
            <AccountSharing me={me} />
          </Section>
        </>
      )}
    </div>
  )
}

function SlotMaskGrid({ mask, onChange, mobile }: {
  mask: SlotMask | null
  onChange: (m: SlotMask) => void
  mobile: boolean
}) {
  const [saving, setSaving] = useState(false)
  if (!mask) {
    return <div style={{ color: theme.inkSoft, fontSize: 13 }}>Loading…</div>
  }
  const cellOf = (dow: number, slot: SlotKey): boolean => {
    const c = mask.cells.find(x => x.day_of_week === dow && x.slot === slot)
    return c ? c.fill : true
  }
  const toggle = async (dow: number, slot: SlotKey) => {
    if (saving) return
    const next: SlotMaskCell[] = mask.cells.map(c =>
      c.day_of_week === dow && c.slot === slot ? { ...c, fill: !c.fill } : c,
    )
    const optimistic: SlotMask = { cells: next }
    onChange(optimistic)
    setSaving(true)
    try {
      const saved = await api.setSlotMask(next)
      onChange(saved)
    } catch (e) {
      console.error(e)
      onChange(mask) // revert
    } finally {
      setSaving(false)
    }
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{
        display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
        marginBottom: 10, fontSize: 12, color: theme.inkSoft, fontFamily: theme.sans,
      }}>
        <LegendSwatch on={true} label="Plan"/>
        <LegendSwatch on={false} label="Skip"/>
      </div>
      <table style={{ borderCollapse: 'separate', borderSpacing: 6, fontFamily: theme.sans }}>
        <thead>
          <tr>
            <th/>
            {DOW_LONG.map((d, i) => (
              <th key={d} style={{
                fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase',
                color: theme.muted, fontWeight: 600, padding: '0 4px',
              }}>
                <span style={{ display: 'none' }} className="dow-long">{d}</span>
                {/* always render short label for compactness */}
                {DOW_SHORT[i]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SLOT_KEYS.map(slot => (
            <tr key={slot}>
              <th style={{
                fontSize: 12, color: theme.inkSoft, fontWeight: 500,
                textAlign: 'right', paddingRight: 8, whiteSpace: 'nowrap',
              }}>{mobile ? slot[0].toUpperCase() : SLOT_LABELS[slot]}</th>
              {[1,2,3,4,5,6,7].map(dow => {
                const on = cellOf(dow, slot)
                return (
                  <td key={dow}>
                    <button
                      onClick={() => toggle(dow, slot)}
                      disabled={saving}
                      aria-label={`${DOW_LONG[dow-1]} ${SLOT_LABELS[slot]}: ${on ? 'fill' : 'skip'}`}
                      style={{
                        all: 'unset', cursor: saving ? 'default' : 'pointer',
                        width: 36, height: 36, display: 'grid', placeItems: 'center',
                        borderRadius: theme.radius,
                        background: on ? theme.ink : theme.surface,
                        color: on ? theme.surface : theme.muted,
                        border: `1px solid ${on ? theme.ink : theme.line}`,
                        fontSize: 14, fontWeight: 600,
                      }}
                    >
                      {on ? '●' : '○'}
                    </button>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function LegendSwatch({ on, label }: { on: boolean; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        width: 22, height: 22, display: 'grid', placeItems: 'center',
        borderRadius: theme.radius,
        background: on ? theme.ink : theme.surface,
        color: on ? theme.surface : theme.muted,
        border: `1px solid ${on ? theme.ink : theme.line}`,
        fontSize: 12, fontWeight: 600,
      }}>{on ? '●' : '○'}</span>
      <span>{label}</span>
    </span>
  )
}
